import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sun, Loader2, Mail, Phone } from "lucide-react";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const HOME_BY_ROLE = { admin: "/dashboard", sales: "/leads", technician: "/services" };

export default function Login() {
  const { login, user } = useAuth();
  const [mode, setMode] = useState("email"); // "email" | "mobile"
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to={HOME_BY_ROLE[user.role] || "/dashboard"} replace />;

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(identifier.trim(), password);
      toast.success("Welcome back!");
      nav(HOME_BY_ROLE[u?.role] || "/dashboard");
    } catch (ex) {
      setErr(formatError(ex.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const modeBtn = (m, Icon, label, testid) => (
    <button type="button" onClick={()=>{setMode(m); setErr(""); setIdentifier(""); setPassword("");}}
      className={`flex items-center justify-center gap-2 rounded-lg border py-2 text-sm font-semibold ${mode===m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
      data-testid={testid}>
      <Icon className="w-4 h-4"/> {label}
    </button>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{background:"linear-gradient(135deg,#0B132B 0%,#1C2541 100%)"}}>
        <img src="https://images.pexels.com/photos/6601008/pexels-photo-6601008.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Sun className="w-6 h-6" strokeWidth={2.5}/>
            </div>
            <div>
              <div className="font-bold text-xl" style={{fontFamily:'Outfit'}}>MRM Renewables</div>
              <div className="text-amber-300/90 text-xs uppercase tracking-widest">Solar CRM Platform</div>
            </div>
          </div>
          <div>
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight mb-4" style={{fontFamily:'Outfit'}}>
              Manage every ray of your solar business.
            </h1>
            <p className="text-slate-300 max-w-md">
              Leads, quotations, projects, services & complaints — all in one place. Built for the Indian solar industry.
            </p>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-400">
            <span>Admin · Sales · Technician</span>
            <span>Hindi + English</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-slate-200 shadow-xl shadow-slate-200/40">
          <CardContent className="p-8">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Sun className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="font-bold text-lg" style={{fontFamily:'Outfit'}}>MRM Renewables</div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1" style={{fontFamily:'Outfit'}}>Sign in</h2>
            <p className="text-sm text-slate-500 mb-4">Sign in with your Email or Mobile number.</p>

            <div className="grid grid-cols-2 gap-2 mb-4" role="tablist">
              {modeBtn("email", Mail, "Email", "login-mode-email")}
              {modeBtn("mobile", Phone, "Mobile", "login-mode-mobile")}
            </div>

            <form onSubmit={submitPassword} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="identifier">{mode==="email" ? "Email" : "Mobile Number"}</Label>
                <Input id="identifier" type={mode==="email"?"email":"tel"} value={identifier}
                  onChange={(e)=>setIdentifier(e.target.value)} required
                  placeholder={mode==="email"?"you@example.com":"9000000001"}
                  data-testid={mode==="email"?"login-email-input":"login-mobile-input"}
                  autoComplete={mode==="email"?"email":"tel"} />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
                  required data-testid="login-password-input" autoComplete="current-password" />
                <div className="mt-1 text-[11px] text-slate-500 flex items-center justify-between">
                  <span>Password reset by mobile + OTP.</span>
                  <a href="/forgot-password" className="text-amber-700 font-semibold hover:underline" data-testid="forgot-password-link">Forgot password?</a>
                </div>
              </div>
              {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
              <Button type="submit" disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="login-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Signing in...</> : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster position="top-right" richColors />
      </div>
    </div>
  );
}
